import React, { useEffect, useRef, useState } from 'react';
import Avatar from '../components/Avatar';
import { useProfile } from './ProfileContext';
import { useProfileData } from './useProfileData';
import { ProfileHeader, ProfileActions, formatMemberSince } from './ProfileParts';
import { ProfileAbout, ProfileLinks, themedCardProps } from './ProfileContent';
import { useLanguage } from '../i18n/LanguageContext';
import { useLayoutMode } from '../hooks/useLayoutMode';
import { imageSrc } from '../utils/avatar';
import logo from '../images/icon_small.png';
import '../style/Profile.css';

// The full profile: header, actions, and tabs - About / mutual friends /
// mutual groups - with every mutual listed (the card shows the first few).
// Shown in a modal (FullProfileModal) and embedded in the Friends page.
//
// seed: what the opener already knows (name, avatar), shown while loading.
// onSelectUser(user): a mutual friend was clicked - the Friends page shows
//   them in place; without it they open in the profile card.
// onChanged: an action changed the relationship - the Friends page reloads
//   its lists and bumps `version`, which reloads this profile; without it
//   the profile just reloads itself.
// onNavigate: an action is leaving the page (message, edit profile).
export default function ProfileView({ username, seed, className = '', onSelectUser, onChanged, onNavigate = () => {}, version, footer }) {
  const { t, locale } = useLanguage();
  const { openProfile } = useProfile();
  // A phone fits one labelled button and the rest as icons, like the card.
  const { isMobile } = useLayoutMode();
  const { status, profile, reload } = useProfileData(username, { full: true });
  const [tab, setTab] = useState('about');
  const p = profile || seed || { username };
  const themed = themedCardProps(p);
  const social = profile && !['self', 'blocked'].includes(profile.relationship);

  const firstVersion = useRef(version);
  useEffect(() => {
    if (version !== firstVersion.current) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  // The tab a person had open doesn't carry over to the next person.
  useEffect(() => setTab('about'), [username]);

  const tabs = [
    { key: 'about', label: t('profile.tabAbout') },
    ...(social ? [
      { key: 'friends', label: `${t('profile.tabFriends')} (${profile.mutualFriendCount})` },
      { key: 'groups', label: `${t('profile.tabGroups')} (${profile.mutualGroupCount})` }
    ] : [])
  ];

  const pickMutual = (e, f) => (onSelectUser ? onSelectUser(f) : openProfile(f.username, e.currentTarget, f));

  return (
    <div className={`ProfileFull${themed.className}${className ? ` ${className}` : ''}`} style={themed.style}>
      <ProfileHeader p={p} big />
      <div className="ProfileFullBody">
        {status === 'notfound' && <p className="descGray">{t('profile.notFound')}</p>}
        {status === 'error' && <p className="descGray">{t('profile.loadFailed')}</p>}
        {profile && <ProfileActions profile={profile} onChanged={onChanged || reload} onNavigate={onNavigate} compact={isMobile} />}

        {profile && (
          <>
            <div className="ProfileTabs" role="tablist">
              {tabs.map((x) => (
                <button key={x.key} type="button" role="tab" aria-selected={tab === x.key}
                  className={`ProfileTab${tab === x.key ? ' ProfileTabActive' : ''}`} onClick={() => setTab(x.key)}>
                  {x.label}
                </button>
              ))}
            </div>

            {tab === 'about' && (
              <div className="ProfileTabPanel">
                {profile.description && <p className="ProfileBio">{profile.description}</p>}
                <ProfileAbout text={profile.aboutMe} />
                <ProfileLinks links={profile.links} />
                {profile.memberSince && (
                  <div className="ProfileSince descGray">{t('profile.memberSince', { date: formatMemberSince(profile.memberSince, locale) })}</div>
                )}
              </div>
            )}

            {tab === 'friends' && (
              <ul className="ProfileList">
                {profile.mutualFriends.length === 0 && <li className="descGray">{t('profile.noMutualFriends')}</li>}
                {profile.mutualFriends.map((f) => (
                  <li key={f.username}>
                    <button type="button" className="ProfileListItem" onClick={(e) => pickMutual(e, f)}>
                      <Avatar imageUrl={f.imageUrl} alt="" />
                      <span>{f.fullName || f.username}</span>
                      <span className="descGray">@{f.username}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {tab === 'groups' && (
              <ul className="ProfileList">
                {profile.mutualGroups.length === 0 && <li className="descGray">{t('profile.noMutualGroups')}</li>}
                {profile.mutualGroups.map((g) => (
                  <li key={g.groupId} className="ProfileListItem">
                    <img src={g.imageUrl && g.imageUrl !== 'group.png' ? imageSrc(g.imageUrl) : logo} alt="" />
                    <span>{g.name}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
      {footer}
    </div>
  );
}
